export type SopMdBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'image'; frameId: string; caption: string };

function stripInline(text: string): string {
  return text
    .replace(/\[\[(c\d+)\]\]/g, (_m, id: string) => `[${id.slice(1)}]`)
    .replace(/!\[([^\]]*)\]\(frame:[^)]+\)/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
}

function splitTableRow(line: string): string[] {
  return line
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => stripInline(c.trim()));
}

function isTableSep(line: string): boolean {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim());
}

function isHeading(line: string): RegExpMatchArray | null {
  return line.trim().match(/^(#{1,6})\s+(.+)$/);
}

/** Split a SOP section body into simple blocks for PDF layout. */
export function parseSopMarkdown(body: string): SopMdBlock[] {
  const lines = (body || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: SopMdBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const heading = isHeading(trimmed);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: stripInline(heading[2]) });
      i += 1;
      continue;
    }

    const img = trimmed.match(/^!\[([^\]]*)\]\(frame:([^)]+)\)$/);
    if (img) {
      blocks.push({ type: 'image', caption: img[1].trim(), frameId: img[2].trim() });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('|') && i + 1 < lines.length && isTableSep(lines[i + 1].trim())) {
      const headers = splitTableRow(trimmed);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && !isTableSep(lines[i].trim())) {
        rows.push(splitTableRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    const ul = trimmed.match(/^[-*+]\s+(.*)$/);
    if (ul) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].trim().match(/^[-*+]\s+(.*)$/);
        if (!m) break;
        items.push(stripInline(m[1]));
        i += 1;
      }
      blocks.push({ type: 'list', ordered: false, items });
      continue;
    }

    const ol = trimmed.match(/^\d+[.)]\s+(.*)$/);
    if (ol) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].trim().match(/^\d+[.)]\s+(.*)$/);
        if (!m) break;
        items.push(stripInline(m[1]));
        i += 1;
      }
      blocks.push({ type: 'list', ordered: true, items });
      continue;
    }

    const para: string[] = [];
    while (i < lines.length) {
      const t = lines[i].trim();
      if (
        !t ||
        t.startsWith('|') ||
        /^[-*+]\s+/.test(t) ||
        /^\d+[.)]\s+/.test(t) ||
        /^!\[/.test(t) ||
        isHeading(t)
      ) {
        break;
      }
      para.push(t);
      i += 1;
    }
    const text = stripInline(para.join(' '));
    if (text) blocks.push({ type: 'paragraph', text });
  }

  return blocks;
}
